import TodoListItem, { TodoListItemProps, TodoListItemHandle } from './TodoListItem';
import { TodoItemDragData } from 'owa-dnd/lib/utils/TodoItemDragData';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
    DragAndDroppable,
    DragData,
    DragViewState,
    DropViewState,
    createDragViewState,
    createDropViewState,
    DraggableItemTypes,
} from 'owa-dnd';
import { isBrowserFirefox } from 'owa-user-agent';
import { TodoListItemConfig } from '../schema/TodoListItemConfig';

const styles = require('./TodoListItemDraggable.scss');
const classNames = require('classnames/bind').bind(styles);

export interface TodoListItemDraggableProps extends TodoListItemProps {
    canDropOver: boolean;
    todoListItemConfig?: TodoListItemConfig;
    forwardedRef?: React.Ref<TodoListItemHandle>;
}

const DRAG_X_OFFSET = -10;
const DRAG_Y_OFFSET = 40;

export default observer(function TodoListItemDraggable(props: TodoListItemDraggableProps) {
    props = {
        canDropOver: true,
        ...props,
    };
    const [dropViewState, setDropViewState] = React.useState<DropViewState>(createDropViewState());
    const [dragViewState] = React.useState<DragViewState>(createDragViewState());

    const onDragOver = React.useCallback(
        (dragInfo: DragData) =>
            setDropViewState({
                isDragOver: true,
                draggableItemType: dragInfo.itemType,
            }),
        []
    );

    const onDragLeaveOrEnd = React.useCallback(
        (_: DragData) =>
            setDropViewState({
                isDragOver: false,
                draggableItemType: '',
            }),
        []
    );
    const isEditing = props.isBeingEdited(props.todo.Id);

    const canDrag = React.useCallback(() => !isEditing, [isEditing]);
    const { reorderTodos } = props.callbacks;
    const isDraggable = React.useCallback(() => {
        /**
         * In Firefox, when a textarea is draggable, you are no longer able to click
         * to move the cursor within a textarea. This bug is tracked here: https://bugzilla.mozilla.org/show_bug.cgi?id=943944
         * A textarea is rendered by the EditableTitle component when isEditing is false,
         * so when isEditing, we want to disable dragging for the todo.
         */
        return !(isBrowserFirefox() && isEditing);
    }, [isEditing]);

    const getDragData = React.useCallback(() => {
        let dragData: TodoItemDragData = {
            itemType: DraggableItemTypes.Todo,
            itemData: {
                subject: props.todo.Subject,
                taskId: props.todo.Id,
            },
        };
        return dragData;
    }, [props.todo.Id, props.todo.Subject]);

    const onDrop = React.useCallback(
        (dragData: DragData, pageX: number, pageY: number, target?: HTMLElement) => {
            switch (dragData.itemType) {
                case DraggableItemTypes.Todo:
                    const { itemData } = dragData as TodoItemDragData;
                    if (itemData.taskId) {
                        reorderTodos(itemData.taskId, props.todo.Id);
                    }
                    break;
            }
        },
        [props.todo.Id]
    );

    const canDrop = React.useCallback(
        (dragInfo: DragData): boolean => {
            return props.canDropOver && dragInfo.itemType === DraggableItemTypes.Todo;
        },
        [props.canDropOver]
    );

    const getDragPreview = React.useCallback(
        (todoDragData: TodoItemDragData) => {
            var elem = document.createElement('div');
            ReactDOM.render(
                <div className={styles.dragHelperStyle}>
                    <TodoListItem
                        callbacks={props.callbacks}
                        isBeingEdited={props.isBeingEdited}
                        showTodoListItemInfo={props.showTodoListItemInfo}
                        todo={props.todo}
                        titleClassName={styles.dragTextWrapper}
                        checkboxClassName={styles.dragCheckboxClassName}
                    />
                </div>,
                elem
            );
            return elem;
        },
        [props.todo]
    );

    const { forwardedRef, todo, ...passThroughProps } = props;
    const isDragOver =
        props.canDropOver && dropViewState.isDragOver && !dragViewState.isBeingDragged;

    passThroughProps.todoClassName = classNames(passThroughProps.todoClassName, {
        [styles.topBorderDragOver]: isDragOver,
    });
    return (
        <DragAndDroppable
            isDraggable={isDraggable}
            getDragData={getDragData}
            getDragPreview={getDragPreview}
            showDefaultNoDragImage={false}
            xOffset={DRAG_X_OFFSET}
            yOffset={DRAG_Y_OFFSET}
            dragViewState={dragViewState}
            dropViewState={dropViewState}
            canDrop={canDrop}
            onDrop={onDrop}
            canDrag={canDrag}
            onDragOver={onDragOver}
            onDragLeave={onDragLeaveOrEnd}
            onDragEnd={onDragLeaveOrEnd}
            classNames={classNames({
                isBeingDragged: dragViewState.isBeingDragged,
            })}
            preventBubblingDragEvents={true}
            {...props.todoListItemConfig?.dragDropProps}>
            <TodoListItem ref={forwardedRef} todo={todo} {...passThroughProps} />
        </DragAndDroppable>
    );
});
